import { CircleAlert, CircleCheck, UserRoundSearch, type LucideIcon } from "lucide-react"
import { useNavigate } from "react-router"

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"

import type { ReviewVersion } from "../api"
import type { ReviewId } from "../data/review-data"

import type { Report, ReportVerdict } from "./report-data"

interface VerdictPresentation {
  headline: string
  className: string
  Icon: LucideIcon
}

const VERDICT: Record<ReportVerdict, VerdictPresentation> = {
  clear: {
    headline: "No divergences requiring action",
    className: "border-master/30 bg-master-tint text-master",
    Icon: CircleCheck,
  },
  issues_found: {
    headline: "Divergences from the approved master",
    className: "border-err/30 bg-err-tint text-err",
    Icon: CircleAlert,
  },
  needs_sme: {
    headline: "Referred to a specialist",
    className: "border-people/30 bg-people-tint text-people",
    Icon: UserRoundSearch,
  },
}

/**
 * The report's title block: what was reviewed, which version, and the verdict.
 *
 * The version switcher keeps the reader on the same tab of the other version's
 * report, so comparing two versions is a matter of flipping between them.
 */
export function ReportMasthead({
  report,
  reviewId,
  versions,
  tab,
}: {
  report: Report
  reviewId: ReviewId
  versions: readonly ReviewVersion[]
  tab: string
}): JSX.Element {
  const navigate = useNavigate()
  const verdict = VERDICT[report.verdict]
  const ordered = [...versions].sort((a, b) => a.versionNo - b.versionNo)

  return (
    <header className="flex flex-col items-center text-center">
      <img src="/pyxis-wordmark.png" alt="PYXIS Regulatory Consulting" className="h-9 w-auto" />
      <h1 className="mt-8 text-title text-text">Label Review</h1>
      <p className="mt-2 text-subtitle font-normal text-text-2">
        {report.productName ?? "Product"} <span className="text-text-3">·</span> EPA Reg.{" "}
        {report.regNo}
      </p>
      <p className="mt-1 text-eyebrow uppercase text-brand">
        EPA master label vs final printed label — divergence review
      </p>

      {ordered.length > 1 ? (
        <Select
          value={reviewId}
          onValueChange={(next) => {
            if (next !== reviewId) void navigate(`/reviews/${next}/output?view=${tab}`)
          }}
        >
          <SelectTrigger
            selectSize="sm"
            aria-label="Version"
            className="mt-3 w-auto rounded-full px-3 text-eyebrow font-semibold uppercase"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="min-w-40">
            {ordered.map((v) => (
              <SelectItem key={v.reviewId} value={v.reviewId}>
                Version {v.versionNo}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        report.versionNo > 1 && (
          <span className="mt-3 inline-flex items-center rounded-full border border-border bg-surface-sunken px-3 py-1 text-eyebrow font-semibold uppercase text-text-2">
            Version {report.versionNo}
          </span>
        )
      )}

      <div aria-hidden="true" className="mt-3 h-px w-24 bg-brand" />

      <div className="mt-5 flex flex-col items-center gap-1.5">
        <span className="text-eyebrow uppercase text-text-3">Verdict</span>
        <span
          data-verdict={report.verdict}
          className={cn(
            "inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-body-sm font-semibold",
            verdict.className,
          )}
        >
          <verdict.Icon className="size-4" aria-hidden="true" />
          {verdict.headline}
        </span>
      </div>
    </header>
  )
}
